import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { usePublicMeta } from "@/lib/publicMeta";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";

/**
 * Sign-in is the one screen shown before authentication, so it reads the
 * business name from the public /api/meta endpoint rather than the logged-in
 * settings. Each deployment brands itself with no code change.
 */
export function AuthShell({ children, heading, sub, footer }) {
  // Remembered from last time, so the platform name doesn't flash the default
  // before the real one arrives.
  const brand = usePublicMeta();

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-surface">
      {/* Brand side */}
      <div className="hidden lg:flex flex-col justify-between bg-brand text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          {/* Goes home, same as the landing page wordmark. */}
          <Link to="/" className="inline-flex items-center gap-3 group" aria-label="Home">
            {brand.logo && (
              <img src={brand.logo} alt="" className="w-9 h-9 rounded-[10px] object-contain bg-white/10" />
            )}
            <div className="font-display text-[26px] font-semibold tracking-tight group-hover:text-white/85 transition-colors">
              {brand.platform_name || "Messages"}
            </div>
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1.5">
            Messages &amp; files
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="font-display text-[34px] leading-[1.15] font-semibold tracking-[-0.02em]">
            {brand.public?.login?.tagline || "Messages and files — in one place."}
          </p>
          <p className="mt-4 text-white/70 text-[15px] leading-relaxed">
            {brand.public?.login?.body}
          </p>
          <div className="mt-10 grid grid-cols-4 gap-x-5 gap-y-2.5 font-mono text-[11px] text-white/50">
            {["DEV", "DES", "PM", "QA", "OPS", "BIZ", "ADM", "SUP"].map((p) => (
              <div key={p} className="flex items-center gap-1.5">
                <span className="text-white/85 font-semibold">{p}</span>
                <span className="h-px flex-1 bg-white/20" />
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 font-mono text-[11px] text-white/40">
          Messages and files, together
        </div>
        <div
          className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full opacity-[0.14]"
          style={{ background: "radial-gradient(circle,#F6D9A8 0%,transparent 65%)" }}
        />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[380px] animate-rise">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-block" aria-label="Home">
              <div className="font-display text-[22px] font-semibold text-ink">
                {brand.platform_name || "Messages"}
              </div>
            </Link>
            <div className="eyebrow mt-1">Messages &amp; files</div>
          </div>
          <h1 className="font-display text-[26px] font-semibold tracking-[-0.01em] text-ink">
            {heading}
          </h1>
          <p className="text-[13.5px] text-muted2 mt-1.5">{sub}</p>
          <div className="mt-7">{children}</div>
          {typeof footer === "function" ? footer(brand) : footer}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [pin, setPin] = useState("");
  // Who (if anyone) this specific browser is recognized as — resolved from
  // the device_id cookie server-side, never typed. null while checking,
  // {available:false} for a fresh/unrecognized device or one whose account
  // doesn't have a PIN, {available:true, name} once quick-unlock can show.
  const [deviceAccount, setDeviceAccount] = useState(null);
  // "password" is the default and always available. "pin" is offered
  // automatically — never via a manual toggle — the moment the device check
  // above comes back recognized, so nobody has to type an email to use it.
  const [mode, setMode] = useState("password");
  const [busy, setBusy] = useState(false);
  const [signedOutReason, setSignedOutReason] = useState("");
  const meta = usePublicMeta();
  const copy = meta.public?.login || {};
  const heading = copy.heading || "Sign in";
  const sub = copy.sub || "Pick up where the count left off.";

  // If access was withdrawn mid-session, say why rather than dropping them at a
  // bare sign-in form wondering what happened.
  useEffect(() => {
    try {
      const reason = window.sessionStorage.getItem("signed_out_reason");
      if (reason) {
        setSignedOutReason(reason);
        window.sessionStorage.removeItem("signed_out_reason");
      }
    } catch {
      /* private browsing */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/device-account")
      .then(({ data }) => {
        if (cancelled) return;
        setDeviceAccount(data);
        if (data.available) setMode("pin");
      })
      .catch(() => !cancelled && setDeviceAccount({ available: false }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login-pin", { pin });
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      heading={heading}
      sub={sub}
      footer={(brand) =>
        brand.needs_setup ? (
          <p className="mt-6 text-[13px] text-muted2">
            First time here?{" "}
            <Link to="/register" className="link-quiet font-medium">
              Set up the platform
            </Link>
          </p>
        ) : null
      }
    >
      {signedOutReason && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-gold-light px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-gold leading-relaxed">{signedOutReason}</p>
        </div>
      )}
      {mode === "pin" && deviceAccount?.available ? (
        <form onSubmit={submitPin} className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-brand-light text-brand flex items-center justify-center font-display font-semibold text-[16px] shrink-0">
              {(deviceAccount.name?.[0] || "?").toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-display text-[16px] font-semibold text-ink leading-tight truncate">
                Continue as {deviceAccount.name}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pin">Enter your PIN</Label>
            <PasswordInput
              id="pin"
              inputMode="text"
              autoFocus
              required
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy || !pin} className="w-full bg-brand hover:bg-brand-hover h-11">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setMode("password");
                setPin("");
              }}
              className="link-quiet text-[12.5px]"
            >
              Not you? Use email and password
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover h-11">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
          <div className="flex items-center justify-between">
            {deviceAccount?.available && (
              <button
                type="button"
                onClick={() => setMode("pin")}
                className="link-quiet text-[12.5px]"
              >
                Use your PIN instead
              </button>
            )}
            <Link to="/forgot-password" className="link-quiet text-[12.5px] ml-auto">
              Forgotten your password?
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
